import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isAcceptablePassword } from '@il-colpo/shared/account';
import { useAccount } from '../../account/accountStore.ts';
import { useCaptcha, type Captcha } from '../../account/captcha.tsx';
import type { AccountClient, AuthMethods, CodePurpose } from '../../account/types.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { Button, Panel, Tabs, TextField } from '../../ui/index.ts';
import styles from './Account.module.css';
import { FormError, FormNote, useAction, useAuthMethods } from './common.tsx';

/**
 * Getting in: e-mail and password, a one-time code by e-mail, a provider, or as a guest. The
 * screens it walks through (a code to confirm, a password to reset) are views of this one panel.
 */

type View =
  | { name: 'main'; tab: 'signIn' | 'signUp' }
  | { name: 'codeRequest' }
  | { name: 'code'; email: string; purpose: CodePurpose }
  | { name: 'resetRequest' };

const digits = (value: string): string => value.replace(/\D/g, '').slice(0, 6);

interface FormProps {
  client: AccountClient;
  captcha: Captcha;
  go: (view: View) => void;
  methods: AuthMethods;
}

function SignInForm({ client, captcha, go, methods }: FormProps) {
  const { t } = useTranslation('account');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { pending, error, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await run(() => client.signIn(email.trim(), password, captcha.token));
    captcha.reset();
    if (!r.ok && r.error.code === 'emailNotConfirmed') {
      // The address was never confirmed: send the player where the code is asked for.
      const again = await client.resendCode(email.trim(), 'signUp');
      if (again.ok) go({ name: 'code', email: email.trim(), purpose: 'signUp' });
    }
  };

  return (
    <form className={styles['form']} onSubmit={(e) => void submit(e)} noValidate>
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        inputMode="email"
        value={email}
        onChange={setEmail}
      />
      <TextField
        label={t('auth.password')}
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
      />
      <FormError code={error} />
      <Button
        type="submit"
        loading={pending}
        disabled={!captcha.ready || !email || !password}
        block
      >
        {t('auth.signIn')}
      </Button>
      {methods.code ? (
        <>
          <button
            type="button"
            className={styles['linkButton']}
            onClick={() => {
              go({ name: 'resetRequest' });
            }}
          >
            {t('auth.forgot')}
          </button>
          <button
            type="button"
            className={styles['linkButton']}
            onClick={() => {
              go({ name: 'codeRequest' });
            }}
          >
            {t('auth.useCode')}
          </button>
        </>
      ) : null}
    </form>
  );
}

function SignUpForm({ client, captcha, go, methods }: FormProps) {
  const { t } = useTranslation('account');
  const { language } = useSettings();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stuck, setStuck] = useState(false);
  const { pending, error, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setStuck(false);
    const r = await run(() =>
      client.signUp(email.trim(), password, { language, captchaToken: captcha.token }),
    );
    captcha.reset();
    if (r.ok && r.value.needsConfirmation) {
      // The project wants the address confirmed, and this build has no way to receive a code: say so
      // instead of asking for a code that will never come.
      if (methods.code) go({ name: 'code', email: email.trim(), purpose: 'signUp' });
      else setStuck(true);
    }
  };

  return (
    <form className={styles['form']} onSubmit={(e) => void submit(e)} noValidate>
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        inputMode="email"
        value={email}
        onChange={setEmail}
      />
      <TextField
        label={t('auth.password')}
        type="password"
        autoComplete="new-password"
        hint={t('auth.passwordHint')}
        value={password}
        onChange={setPassword}
      />
      <FormError code={error} />
      {stuck ? (
        <p className={styles['error']} role="alert">
          {t('auth.confirmUnavailable')}
        </p>
      ) : null}
      <Button
        type="submit"
        loading={pending}
        disabled={!captcha.ready || !email || !isAcceptablePassword(password)}
        block
      >
        {t('auth.signUp')}
      </Button>
      <p className={styles['note']}>{t('auth.terms')}</p>
    </form>
  );
}

/** Asks for an address to send a sign-in code to. */
function CodeRequestForm({ client, captcha, go }: FormProps) {
  const { t } = useTranslation('account');
  const { language } = useSettings();
  const [email, setEmail] = useState('');
  const { pending, error, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await run(() =>
      client.sendCode(email.trim(), { language, captchaToken: captcha.token }),
    );
    captcha.reset();
    if (r.ok) go({ name: 'code', email: email.trim(), purpose: 'signIn' });
  };

  return (
    <form className={styles['form']} onSubmit={(e) => void submit(e)} noValidate>
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        inputMode="email"
        value={email}
        onChange={setEmail}
      />
      <FormError code={error} />
      <Button type="submit" loading={pending} disabled={!captcha.ready || !email} block>
        {t('auth.sendCode')}
      </Button>
      <button
        type="button"
        className={styles['linkButton']}
        onClick={() => {
          go({ name: 'main', tab: 'signIn' });
        }}
      >
        {t('auth.usePassword')}
      </button>
    </form>
  );
}

/** Asks for the six digits an e-mail carried. */
function CodeForm({
  client,
  email,
  purpose,
  go,
  onRecovered,
}: {
  client: AccountClient;
  email: string;
  purpose: CodePurpose;
  go: (view: View) => void;
  onRecovered: () => void;
}) {
  const { t } = useTranslation('account');
  const { language } = useSettings();
  const [code, setCode] = useState('');
  const [resent, setResent] = useState(false);
  const { pending, error, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await run(() => client.verifyCode(email, code, purpose));
    if (r.ok && purpose === 'recovery') onRecovered();
  };

  const resend = async () => {
    setResent(false);
    const r = await run(() => {
      if (purpose === 'recovery') return client.sendPasswordReset(email);
      if (purpose === 'signIn') return client.sendCode(email, { language });
      return client.resendCode(email, purpose);
    });
    if (r.ok) setResent(true);
  };

  return (
    <form className={styles['form']} onSubmit={(e) => void submit(e)} noValidate>
      <p className={styles['note']}>{t('auth.codeSent', { email })}</p>
      <TextField
        label={t('auth.code')}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(v) => {
          setCode(digits(v));
        }}
      />
      <FormError code={error} />
      {resent ? <FormNote>{t('auth.resent')}</FormNote> : null}
      <Button type="submit" loading={pending} disabled={code.length !== 6} block>
        {t('auth.verify')}
      </Button>
      <div className={styles['row']}>
        <button
          type="button"
          className={styles['linkButton']}
          onClick={() => {
            void resend();
          }}
        >
          {t('auth.resend')}
        </button>
        <button
          type="button"
          className={styles['linkButton']}
          onClick={() => {
            go({ name: 'main', tab: 'signIn' });
          }}
        >
          {t('auth.back')}
        </button>
      </div>
    </form>
  );
}

function ResetRequestForm({ client, captcha, go }: FormProps) {
  const { t } = useTranslation('account');
  const [email, setEmail] = useState('');
  const { pending, error, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await run(() => client.sendPasswordReset(email.trim(), captcha.token));
    captcha.reset();
    if (r.ok) go({ name: 'code', email: email.trim(), purpose: 'recovery' });
  };

  return (
    <form className={styles['form']} onSubmit={(e) => void submit(e)} noValidate>
      <h2 className={styles['title']}>{t('auth.resetTitle')}</h2>
      <p className={styles['note']}>{t('auth.resetBody')}</p>
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        inputMode="email"
        value={email}
        onChange={setEmail}
      />
      <FormError code={error} />
      <Button type="submit" loading={pending} disabled={!captcha.ready || !email} block>
        {t('auth.resetSend')}
      </Button>
      <button
        type="button"
        className={styles['linkButton']}
        onClick={() => {
          go({ name: 'main', tab: 'signIn' });
        }}
      >
        {t('auth.back')}
      </button>
    </form>
  );
}

export function AuthPanel({
  client,
  onRecovered,
}: {
  client: AccountClient;
  onRecovered: () => void;
}) {
  const { t } = useTranslation('account');
  const { t: tl } = useTranslation('legal');
  const { language } = useSettings();
  const siteKey = useAccount((s) => s.setup.turnstileSiteKey);
  const captcha = useCaptcha(siteKey);
  const methods = useAuthMethods();
  const [view, setView] = useState<View>({ name: 'main', tab: 'signIn' });
  const guest = useAction();
  const provider = useAction();

  const form = { client, captcha, go: setView, methods };

  let body;
  switch (view.name) {
    case 'main':
      body = methods.password ? (
        <Tabs
          label={t('title')}
          active={view.tab}
          onChange={(tab) => {
            setView({ name: 'main', tab });
          }}
          tabs={[
            { key: 'signIn', label: t('auth.tabs.signIn') },
            { key: 'signUp', label: t('auth.tabs.signUp') },
          ]}
        >
          {view.tab === 'signIn' ? <SignInForm {...form} /> : <SignUpForm {...form} />}
        </Tabs>
      ) : (
        <p className={styles['note']}>
          {methods.providers.length > 0 ? t('auth.providersOnly') : t('auth.guestOnly')}
        </p>
      );
      break;
    case 'codeRequest':
      body = <CodeRequestForm {...form} />;
      break;
    case 'code':
      body = (
        <CodeForm
          client={client}
          email={view.email}
          purpose={view.purpose}
          go={setView}
          onRecovered={onRecovered}
        />
      );
      break;
    case 'resetRequest':
      body = <ResetRequestForm {...form} />;
      break;
  }

  return (
    <>
      <Panel title={view.name === 'code' ? t('auth.codeTitle') : t('title')}>
        {body}
        {captcha.widget}
      </Panel>

      {view.name === 'main' && methods.providers.length > 0 ? (
        <Panel title={t('auth.providers')}>
          <div className={styles['providers']}>
            {methods.providers.map((p) => (
              <Button
                key={p}
                variant="secondary"
                loading={provider.pending}
                onClick={() => {
                  void provider.run(() => client.signInWithProvider(p));
                }}
              >
                {t(`auth.provider.${p}`)}
              </Button>
            ))}
          </div>
          <FormError code={provider.error} />
        </Panel>
      ) : null}

      {view.name === 'main' ? (
        <Panel title={t('auth.guestTitle')}>
          <div className={styles['stack']}>
            <p className={styles['note']}>
              {methods.code || methods.providers.length > 0
                ? t('auth.guestBody')
                : t('auth.guestBodyOnly')}
            </p>
            <FormError code={guest.error} />
            <Button
              variant="ghost"
              loading={guest.pending}
              disabled={!captcha.ready}
              onClick={() => {
                void guest
                  .run(() => client.signInAsGuest({ language, captchaToken: captcha.token }))
                  .then(captcha.reset);
              }}
            >
              {t('auth.guest')}
            </Button>
            <p className={styles['note']}>
              <Link className={styles['link']} to="/legal/terms">
                {tl('links.terms')}
              </Link>
              {' · '}
              <Link className={styles['link']} to="/legal/privacy">
                {tl('links.privacy')}
              </Link>
            </p>
          </div>
        </Panel>
      ) : null}
    </>
  );
}
